import { useState, useEffect } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '../components/ui/sheet';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { LogoIcon } from '../components/Logo';
import {
  LayoutDashboard,
  PhoneCall,
  MessageSquare,
  CreditCard,
  User,
  LogOut,
  Menu,
  Clock,
  ChevronRight,
  Bot,
  Receipt,
  CalendarDays,
  LifeBuoy,
} from 'lucide-react';
import { cn } from '../lib/utils';
import { TooltipProvider } from '../components/ui/tooltip';

const navItems = [
  { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/agents', label: 'My Agents', icon: Bot },
  { path: '/appointments', label: 'My Appointments', icon: CalendarDays },
  { path: '/calls', label: 'Call Logs', icon: PhoneCall },
  { path: '/messages', label: 'Messages', icon: MessageSquare },
  { path: '/buy-minutes', label: 'Buy Minutes', icon: CreditCard },
  { path: '/payments/history', label: 'Payments', icon: Receipt },
  { path: '/support', label: 'Support', icon: LifeBuoy },
];

function Sidebar({ className }) {
  const location = useLocation();

  return (
    <nav className={cn("space-y-1", className)}>
      {navItems.map((item) => {
        const isActive =
          location.pathname === item.path ||
          location.pathname.startsWith(`${item.path}/`);
        return (
          <Link
            key={item.path}
            to={item.path}
            className={cn(
              "flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200",
              isActive
                ? "bg-teal-50 text-teal-700"
                : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
            )}
            data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
          >
            <item.icon className="w-5 h-5" />
            {item.label}
            {isActive && <ChevronRight className="w-4 h-4 ml-auto" />}
          </Link>
        );
      })}
    </nav>
  );
}

export default function DashboardLayout() {
  const { user, logout, isAuthenticated, isAdmin, loading } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    if (!loading) {
      if (!isAuthenticated) {
        navigate('/login', { replace: true });
      } else if (isAdmin) {
        // Admin users should not access customer dashboard
        navigate('/admin', { replace: true });
      }
    }
  }, [loading, isAuthenticated, isAdmin, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated || isAdmin) {
    return null;
  }

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <TooltipProvider delayDuration={200}>
    <div className="min-h-screen bg-slate-50" data-testid="dashboard-layout">
      {/* Top Header */}
      <header className="fixed top-0 left-0 right-0 z-40 h-16 bg-white border-b border-slate-200">
        <div className="flex items-center justify-between h-full px-4 lg:px-6">
          <div className="flex items-center gap-4">
            {/* Mobile Menu */}
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild className="lg:hidden">
                <Button variant="ghost" size="icon">
                  <Menu className="w-5 h-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-72 p-0">
                <div className="p-6 border-b border-slate-200">
                  <Link to="/" className="flex items-center gap-3">
                    <LogoIcon className="w-10 h-10" />
                    <span className="text-xl font-jakarta font-bold bg-gradient-to-r from-teal-600 to-cyan-500 bg-clip-text text-transparent">Desqly</span>
                  </Link>
                </div>
                <div className="p-4">
                  <Sidebar />
                </div>
              </SheetContent>
            </Sheet>

            {/* Logo */}
            <Link to="/" className="flex items-center gap-3">
              <LogoIcon className="w-10 h-10" />
              <span className="text-xl font-jakarta font-bold bg-gradient-to-r from-teal-600 to-cyan-500 bg-clip-text text-transparent hidden sm:block">Desqly</span>
            </Link>
          </div>

          {/* Right Side */}
          <div className="flex items-center gap-4">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu-btn">
                  <Avatar className="w-8 h-8">
                    <AvatarImage src={user?.picture} />
                    <AvatarFallback className="bg-teal-100 text-teal-700">
                      {user?.name?.charAt(0) || 'U'}
                    </AvatarFallback>
                  </Avatar>
                  <span className="hidden sm:block text-sm font-medium">{user?.name}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem asChild>
                  <Link to="/profile" className="flex items-center gap-2">
                    <User className="w-4 h-4" />
                    Profile
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="logout-btn">
                  <LogOut className="w-4 h-4 mr-2" />
                  Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {/* Sidebar - Desktop */}
      <aside className="hidden lg:flex fixed left-0 top-16 bottom-0 w-64 bg-white border-r border-slate-200 flex-col">
        <div className="flex-1 p-4 overflow-y-auto">
          <Sidebar />
        </div>
        <div className="p-4 border-t border-slate-200">
          <div className="p-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl">
            <div className="flex items-center gap-2 text-blue-700 mb-2">
              <Clock className="w-4 h-4" />
              <span className="text-sm font-medium">Quick Tip</span>
            </div>
            <p className="text-xs text-slate-600">
              Purchase minute packages for extra call capacity when you need it.
            </p>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:ml-64 pt-16 min-h-screen flex flex-col">
        <div className="flex-1 p-6 lg:p-8">
          <Outlet />
        </div>
      </div>
    </div>
    </TooltipProvider>
  );
}
